import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useRequireScope } from '@/Hooks/useRequireScope';
import { apiMessage, listOf, setupApi } from '../spacesLib';
import type { Area, Branch, Floor, VenueTable } from '../spacesLib';

interface RungState {
  loading: boolean;
  error: string | null;
}

export interface SpacesChain {
  branches: Branch[];
  branchId: string;
  setBranchId: (id: string) => void;
  /**
   * Only `loading` is ever set — a branch list that cannot be read leaves the
   * rung empty, and the screens above turn that into "there is no branch yet",
   * which is the same instruction either way. It exists so a caller can tell
   * "still asking" from "asked, and there are none": those two look identical
   * and mean opposite things.
   */
  branchesState: RungState;

  floors: Floor[];
  floorId: string | null;
  setFloorId: (id: string | null) => void;
  floorsState: RungState;
  loadFloors: () => void;

  areas: Area[];
  areaId: string | null;
  setAreaId: (id: string | null) => void;
  areasState: RungState;
  loadAreas: () => void;

  tables: VenueTable[];
  tableId: string | null;
  setTableId: (id: string | null) => void;
  tablesState: RungState;
  loadTables: () => void;

  /** Why writing at this rung would be refused, or null when it would be taken. */
  scopeBlock: (level: 'BRANCH' | 'FLOOR' | 'AREA' | 'TABLE', id: string | null | undefined) => string | null;
}

/**
 * §5 — the spaces chain, loaded once and shared: branch › floor › area › table.
 *
 * Pulled out of `SpacesPanel` when the setup wizard grew floor, area and table
 * steps of its own. The three steps are three views of ONE chain — adding a
 * floor and then an area only works if the area step already knows which floor
 * was just made — so the state cannot live in the step component, which
 * unmounts every time the operator moves on.
 *
 * It is a hook rather than a second copy for the reason two copies always are:
 * the cascade below is the subtle part. Changing a parent invalidates every
 * child list under it, and a child list left attached to the wrong parent is
 * not an empty screen — it is somebody else's tables shown as this area's.
 *
 * @param autoSelect Advance the selection to the first row of a rung that has
 *   none. The wizard wants it (a step whose parent is unchosen can only offer a
 *   disabled Add button), the Spaces tab does not: there, picking a floor is how
 *   the operator says which one they are looking at, and choosing for them moves
 *   the two columns beside it out from under them.
 */
export function useSpacesChain(autoSelect = false): SpacesChain {
  const { t } = useTranslation();
  const inScope = useRequireScope();

  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchId, setBranchId] = useState('');
  const [branchesState, setBranchesState] = useState<RungState>({ loading: true, error: null });

  const [floors, setFloors] = useState<Floor[]>([]);
  const [floorId, setFloorId] = useState<string | null>(null);
  const [floorsState, setFloorsState] = useState<RungState>({ loading: false, error: null });

  const [areas, setAreas] = useState<Area[]>([]);
  const [areaId, setAreaId] = useState<string | null>(null);
  const [areasState, setAreasState] = useState<RungState>({ loading: false, error: null });

  const [tables, setTables] = useState<VenueTable[]>([]);
  const [tableId, setTableId] = useState<string | null>(null);
  const [tablesState, setTablesState] = useState<RungState>({ loading: false, error: null });

  useEffect(() => {
    api
      .get('/branches', { params: { limit: 100 } })
      .then(({ data }) => {
        const rows = listOf<Branch>(data);
        setBranches(rows);
        setBranchId((current) => current || rows[0]?.id || '');
      })
      .catch(() => setBranches([]))
      .finally(() => setBranchesState({ loading: false, error: null }));
  }, []);

  const loadFloors = useCallback(() => {
    if (!branchId) return;
    setFloorsState({ loading: true, error: null });
    setupApi.floors
      .list(branchId)
      .then(({ data }) => {
        const rows = listOf<Floor>(data);
        setFloors(rows);
        if (autoSelect) setFloorId((current) => current ?? rows[0]?.id ?? null);
        setFloorsState({ loading: false, error: null });
      })
      .catch((e) =>
        setFloorsState({ loading: false, error: apiMessage(e, t('setup.floor.loadFailed')) }),
      );
  }, [branchId, t, autoSelect]);

  const loadAreas = useCallback(() => {
    if (!floorId) return;
    setAreasState({ loading: true, error: null });
    setupApi.areas
      .list(floorId)
      .then(({ data }) => {
        const rows = listOf<Area>(data);
        setAreas(rows);
        if (autoSelect) setAreaId((current) => current ?? rows[0]?.id ?? null);
        setAreasState({ loading: false, error: null });
      })
      .catch((e) =>
        setAreasState({ loading: false, error: apiMessage(e, t('setup.area.loadFailed')) }),
      );
  }, [floorId, t, autoSelect]);

  const loadTables = useCallback(() => {
    if (!areaId) return;
    setTablesState({ loading: true, error: null });
    setupApi.tables
      .list(areaId)
      .then(({ data }) => {
        setTables(listOf<VenueTable>(data));
        setTablesState({ loading: false, error: null });
      })
      .catch((e) =>
        setTablesState({ loading: false, error: apiMessage(e, t('setup.table.loadFailed')) }),
      );
  }, [areaId, t]);

  // Changing a parent invalidates everything below it, so clear rather than
  // leave a stale child list attached to the wrong parent.
  useEffect(() => {
    setFloorId(null);
    setAreas([]);
    setAreaId(null);
    setTables([]);
    loadFloors();
  }, [branchId, loadFloors]);

  useEffect(() => {
    setAreaId(null);
    setTables([]);
    setTableId(null);
    if (floorId) loadAreas();
    else setAreas([]);
  }, [floorId, loadAreas]);

  useEffect(() => {
    setTableId(null);
    if (areaId) loadTables();
    else setTables([]);
  }, [areaId, loadTables]);

  /**
   * §24.4 — the other reason a write here is refused, and the one nothing on
   * these screens was saying. `PATCH`/`DELETE /floors/:id` declare
   * `@RequireScope(FLOOR,'id')`, `/areas/:id` AREA and `/tables/:id` TABLE,
   * while create is scoped to the *parent* the new row lands in:
   * `POST /floors` → `@RequireScope(BRANCH,'branchId')`, `POST /areas` → FLOOR,
   * `POST /tables` → AREA. None of the list routes carry a scope, so a
   * floor-scoped supervisor is handed the whole tenant's hierarchy and, before
   * this, fully live Add/Edit/Delete buttons on every row of it.
   *
   * `useRequireScope()` answers with the guard's own rule — broader covers
   * narrower, equal level must match the id — so a control is only withdrawn
   * where the server is certain to answer `Out of scope`.
   */
  const scopeBlock = useCallback(
    (level: 'BRANCH' | 'FLOOR' | 'AREA' | 'TABLE', id: string | null | undefined): string | null =>
      inScope(level, id) ? null : t(`setup.outOfScope.${level}`),
    [inScope, t],
  );

  return {
    branches,
    branchId,
    setBranchId,
    branchesState,
    floors,
    floorId,
    setFloorId,
    floorsState,
    loadFloors,
    areas,
    areaId,
    setAreaId,
    areasState,
    loadAreas,
    tables,
    tableId,
    setTableId,
    tablesState,
    loadTables,
    scopeBlock,
  };
}
